import { Page, Project } from 'components/framework/models';
import {
  type DocumentTypeEnum_value,
  type RecordId,
} from 'components/framework/models/generated/types';

interface PageSearchResult {
  id: string;
  path: string;
  name: string;
  parent_id: string;
}

const fetchPageByName = async (
  name: string,
  projectId: RecordId | string
): Promise<PageSearchResult[]> => {
  const response = await fetch(
    `/products/${projectId}/pages/search.json?${new URLSearchParams({
      q: name,
    })}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
    }
  );

  return response.json() as Promise<PageSearchResult[]>;
};

export const findPageById = async (
  id: string,
  documentType: DocumentTypeEnum_value
) => {
  const page = await Page.select('id', 'name', 'documentType')
    .merge({ parent: ['id'] })
    .find(String(id));

  if (page) {
    return new Page({
      id: page.id,
      name: page.name,
      parentId: page.parent?.id,
      documentType: documentType,
    });
  }
};

export const createPage = async (
  name: string | null,
  parentId: RecordId | null,
  documentType: DocumentTypeEnum_value,
  projectId: RecordId | string
) => {
  const page = new Page();
  page.documentType = documentType;
  page.parentId = parentId;
  page.project = new Project({ id: projectId });
  page.name = name;
  await page.save();

  return page;
};

export const findPageByName = async (
  name: string,
  parentId: RecordId | null,
  documentType: DocumentTypeEnum_value,
  projectId: RecordId | string
) => {
  const results = await fetchPageByName(name, projectId);
  const page = results.find(
    page => page.name === name && page.parent_id === parentId
  );
  if (page) {
    return new Page({
      id: page.id,
      name: page.name,
      parentId: page.parent_id,
      documentType: documentType,
    });
  }
};

export const findOrCreatePage = async (
  name: string,
  parentId: RecordId | null,
  documentType: DocumentTypeEnum_value,
  projectId: RecordId | string
) => {
  const page = await findPageByName(name, parentId, documentType, projectId);
  if (page) {
    return page;
  } else {
    return createPage(name, parentId, documentType, projectId);
  }
};

export const openPageNew = async (
  projectId: RecordId | string,
  parent?: Page,
  documentType: string = Page.DOCUMENT_TYPE_INTERNAL_LINK,
  args: any = {}
) => {
  const url = `/products/${projectId}/pages/new.js`;

  await new Promise((resolve, reject) =>
    $.ajax({
      url,
      type: 'GET',
      dataType: 'script',
      data: {
        document_type: documentType,
        record_link_type: 'Page',
        parent_id: parent?.id,
        product_id: projectId,
        ...args,
      },
    })
      .done(resolve)
      .fail(reject)
      .catch(reject)
  );
};
